import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useData } from '../../context/DataContext';
import SectionEditor from '../../components/admin/SectionEditor';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  LayoutDashboard,
  Users,
  Briefcase,
  Quote,
  FileText,
  HelpCircle,
  Phone,
  BarChart3,
  LogOut,
  ChevronRight,
  Menu,
  X,
  Target,
  User,
  Mic,
  GraduationCap
} from 'lucide-react';
import { toast } from 'sonner';

const AdminDashboard = () => {
  const [activeSection, setActiveSection] = useState('heroData');
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const { data, loading, error, refreshData } = useData();
  const navigate = useNavigate();

  const menuItems = [
    { id: 'heroData', label: 'Home: Hero', icon: LayoutDashboard },
    { id: 'statsData', label: 'Home: Stats', icon: BarChart3 },
    { id: 'servicesData', label: 'Home: Services', icon: Briefcase },
    { id: 'frameworkData', label: 'Home: Framework', icon: Target },
    { id: 'whyChooseUsData', label: 'Home: Why Us', icon: HelpCircle },
    { id: 'teamData', label: 'Team', icon: Users },
    { id: 'testimonialsData', label: 'Testimonials', icon: Quote },
    { id: 'blogData', label: 'Blog Posts', icon: FileText },
    { id: 'podcastsData', label: 'Podcasts', icon: Mic },
    { id: 'academyData', label: 'Academy Programs', icon: GraduationCap },
    { id: 'faqData', label: 'FAQs', icon: HelpCircle },
    { id: 'contactInfo', label: 'Contact Info', icon: Phone },
    { id: 'fundraisingPageData', label: 'Page: Fundraising', icon: BarChart3 },
    { id: 'mergersAcquisitionsPageData', label: 'Page: M&A', icon: Briefcase },
    { id: 'businessAdvisoryPageData', label: 'Page: Advisory', icon: Target },
    { id: 'privacyPolicy', label: 'Page: Privacy Policy', icon: Target },
    { id: 'termsAndConditions', label: 'Page: Terms and Conditions', icon: Target },
    { id: 'reportsData', label: 'Reports', icon: FileText },
  ];

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    toast.success('Logged out successfully');
    navigate('/admin/login');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-950 text-white">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-950 text-white p-8 text-center">
        <div>
          <h2 className="text-2xl font-bold text-red-500 mb-4">Error</h2>
          <p className="text-slate-400 mb-6">{error}</p>
          <Button onClick={refreshData} variant="outline" className="text-white border-slate-700">
            Retry
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-slate-950 overflow-hidden text-slate-200">
      {/* Sidebar */}
      <aside
        className={`${isSidebarOpen ? 'w-64' : 'w-20'
          } bg-slate-900 border-r border-slate-800 transition-all duration-300 flex flex-col z-20`}
      >
        <div className="p-6 flex items-center justify-between">
          {isSidebarOpen && (
            <span className="text-xl font-bold bg-gradient-to-r from-amber-400 to-orange-500 bg-clip-text text-transparent">
              Samvedanam
            </span>
          )}
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setIsSidebarOpen(!isSidebarOpen)}
            className="text-slate-400 hover:text-white"
          >
            {isSidebarOpen ? <X size={20} /> : <Menu size={20} />}
          </Button>
        </div>

        <ScrollArea className="flex-1 px-4 py-2">
          <nav className="space-y-1">
            {menuItems.map((item) => (
              <button
                key={item.id}
                onClick={() => setActiveSection(item.id)}
                className={`w-full flex items-center px-3 py-3 rounded-lg transition-all group ${activeSection === item.id
                  ? 'bg-amber-500/10 text-amber-500'
                  : 'text-slate-400 hover:bg-slate-800 hover:text-white'
                  }`}
              >
                <item.icon
                  size={20}
                  className={`${activeSection === item.id ? 'text-amber-500' : 'group-hover:text-white'}`}
                />
                {isSidebarOpen && (
                  <span className="ml-3 font-medium text-sm">{item.label}</span>
                )}
                {isSidebarOpen && activeSection === item.id && (
                  <ChevronRight size={16} className="ml-auto" />
                )}
              </button>
            ))}
          </nav>
        </ScrollArea>

        <div className="p-4 border-t border-slate-800">
          <button
            onClick={handleLogout}
            className="w-full flex items-center px-3 py-3 rounded-lg text-slate-400 hover:bg-red-500/10 hover:text-red-500 transition-all group"
          >
            <LogOut size={20} />
            {isSidebarOpen && <span className="ml-3 font-medium text-sm">Logout</span>}
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 flex flex-col min-w-0 overflow-hidden relative">
        <header className="h-16 border-b border-slate-800 bg-slate-900/50 backdrop-blur-sm flex items-center justify-between px-8 z-10">
          <h1 className="text-lg font-semibold text-white">
            {menuItems.find((m) => m.id === activeSection)?.label}
          </h1>
          <div className="flex items-center gap-4">
            <span className="text-xs text-slate-500 px-2 py-1 border border-slate-800 rounded bg-slate-800/50">
              Admin Access
            </span>
            <div className="w-8 h-8 rounded-full bg-amber-500/20 border border-amber-500/50 flex items-center justify-center">
              <User size={16} className="text-amber-500" />
            </div>
          </div>
        </header>

        <ScrollArea className="flex-1 p-8">
          <div className="max-w-5xl mx-auto">
            <SectionEditor
              sectionId={activeSection}
              sectionData={data[activeSection]}
              onUpdate={refreshData}
            />
          </div>
        </ScrollArea>
      </main>
    </div>
  );
};

export default AdminDashboard;
